import { useEffect, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import { Skeleton, Switch, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { lighten, useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";

import constants from "../../assets/constants";
import { loadConversation } from "../../data/api/omniBotpressAPI";
import OmniChatBubble from "../ui/omniChatBubble";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";
import OmniMenuItem from "../ui/omniMenuItem";
import OmniResponsiveButton from "../ui/omniResponsiveButton";
import OmniTextField from "../ui/omniTextField";
import AlertDialog from "./alertDialog";

// ES Modules import

// Turn a raw Botpress chat message into the shape OmniChatBubble expects.
const formatBotpressMessage = message => {
  let messageText = "";

  if (message.type === "text" || message.type === "quick_reply") {
    messageText += message.payload?.text ?? "";
  } else if (message.type === "choice") {
    messageText += `${message.payload?.text ?? ""}: ${(message.payload?.options ?? [])
      .map(option => option.label)
      .join(" / ")}`;
  } else {
    messageText += `Media message of type ${message.type}`;
  }

  return {
    messageText,
    direction: message.direction,
    date: message.createdAt,
    messageId: message.id,
    hasFeedback: false,
  };
};

const BotpressUserFeedbackDialog = props => {
  const {
    open,
    userFeedbackRecord,
    botODM,
    onClose,
    onAccept,
    onDelete,
    onViewConversation = null,
    dialogTitle = "Edit Feedback",
    okMessage = "Ok",
    cancelMessage = "Cancel",
    maxWidth = "md",
    fullWidth = true,
    deleteMessage = "Delete",
  } = props;

  const theme = useTheme();

  const [editedUserFeedbackRecord] = useState(userFeedbackRecord);

  //input
  const [userFeedback, setUserFeedback] = useState(userFeedbackRecord.comment);
  const [response, setResponse] = useState(userFeedbackRecord.response);
  const [userSentiment, setUserSentiment] = useState(userFeedbackRecord.value);
  const [userName, setUserName] = useState(userFeedbackRecord.userName);
  const [archived, setArchived] = useState(userFeedbackRecord.archived);
  const [status, setStatus] = useState(userFeedbackRecord.status);

  // Chat transcript shown on the left. `null` means still loading; an array
  // (possibly empty) means the load finished. We pull the conversation straight
  // from the Botpress API rather than Redux so this dialog is self-contained.
  const [chatMessages, setChatMessages] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // Fallback bubble built from the feedback record itself, used when we have
    // no conversation to load or the load fails.
    const fallbackMessages = () =>
      userFeedbackRecord.message
        ? [
            {
              messageText: userFeedbackRecord.message,
              direction: "outgoing",
              date: userFeedbackRecord.createdAt ?? null,
              messageId: userFeedbackRecord.messageId ?? "feedback-message",
              hasFeedback: false,
            },
          ]
        : [];

    const load = async () => {
      if (botODM == null || userFeedbackRecord.conversationId == null) {
        setChatMessages(fallbackMessages());
        return;
      }

      try {
        const rawMessages = await loadConversation({
          botODM,
          conversationId: userFeedbackRecord.conversationId,
        });

        const formatted = (rawMessages ?? [])
          .map(formatBotpressMessage)
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        // Locate the bot message this feedback is attached to, then include the
        // user turn that led to it (all consecutive user messages before it).
        const botIndex = formatted.findIndex(m => m.messageId === userFeedbackRecord.messageId);

        let display;
        if (botIndex >= 0) {
          let start = botIndex;
          while (start - 1 >= 0 && formatted[start - 1].direction === "incoming") {
            start -= 1;
          }
          display = formatted.slice(start, botIndex + 1);
        } else {
          display = fallbackMessages();
        }

        if (!cancelled) {
          setChatMessages(display);
        }
      } catch (error) {
        console.error("Failed to load conversation for feedback dialog", error);
        if (!cancelled) {
          setChatMessages(fallbackMessages());
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const [inputTextValid, setUserFeedbackValid] = useState(false);
  // useEffect(() => {
  //   userFeedback ? setUserFeedbackValid(true) : setUserFeedbackValid(false);
  // }, [userFeedback]);

  const [showConfirm, setShowConfirm] = useState(false);

  const onDeleteHandler = () => {
    onDelete(editedUserFeedbackRecord);
  };

  const onAcceptHandler = () => {
    try {
      editedUserFeedbackRecord.comment = userFeedback;
      editedUserFeedbackRecord.value = userSentiment;
      editedUserFeedbackRecord.userName = userName;
      editedUserFeedbackRecord.status = status;
      editedUserFeedbackRecord.archived = archived;
      editedUserFeedbackRecord.response = response;

      //TODO check to see other stuff needs updating
      onAccept(editedUserFeedbackRecord);
    } catch (error) {
      console.log(error);
    }
  };

  const onCloseHandler = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onCloseHandler();
        }
      }}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item size={{ xs: 6 }} sx={{ display: "flex", flexDirection: "column" }}>
            {/* Chat-window style transcript: the user input that led to the
                bot message being reviewed. Read-only bubbles, scrolls when long. */}
            <Box
              sx={{
                position: "relative",
                flexGrow: 1,
                minHeight: "240px",
                marginTop: "8px",
                boxSizing: "border-box",
                borderRadius: "8px",
                backgroundColor: lighten(theme.palette.commonground.contrastText, 0.9),
              }}
            >
              <Box
                component="fieldset"
                aria-hidden
                sx={{
                  position: "absolute",
                  inset: "-5px 0 0 0",
                  margin: 0,
                  padding: "0 8px",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "8px",
                  pointerEvents: "none",
                }}
              >
                <Box
                  component="legend"
                  sx={{
                    ...theme.typography.caption,
                    height: "11px",
                    lineHeight: "11px",
                    padding: "0 4px",
                    visibility: "hidden",
                  }}
                >
                  Conversation
                </Box>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  top: "-9px",
                  left: "10px",
                  px: "4px",
                  backgroundColor: "transparent",
                  color: theme.palette.text.secondary,
                }}
              >
                Conversation
              </Typography>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  cursor: chatMessages == null ? "wait" : "default",
                  padding: theme.spacing(1.5),
                }}
              >
                {chatMessages == null ? (
                  // Skeleton bubbles while the conversation loads.
                  <Grid container flexDirection="column" spacing={1}>
                    <Skeleton
                      variant="rounded"
                      width="80%"
                      height={56}
                      sx={{ alignSelf: "flex-end", borderRadius: "16px 16px 4px 16px" }}
                    />
                    <Skeleton
                      variant="rounded"
                      width="90%"
                      height={96}
                      sx={{ borderRadius: "16px 16px 16px 4px" }}
                    />
                  </Grid>
                ) : chatMessages.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.secondary, textAlign: "center", mt: 2 }}
                  >
                    No conversation available.
                  </Typography>
                ) : (
                  <Grid container flexDirection="column">
                    {chatMessages.map((msg, index) => (
                      <OmniChatBubble
                        key={msg.messageId || index}
                        msg={msg}
                        highlightId={userFeedbackRecord.messageId}
                        readOnly={true}
                      />
                    ))}
                  </Grid>
                )}
              </Box>
            </Box>

            <Button
              onClick={onViewConversation}
              color="secondary"
              variant="outlined"
              disabled={onViewConversation == null}
              sx={{ width: "100%", marginTop: "8px", minHeight: "40px" }}
            >
              View Conversation
            </Button>
          </Grid>
          <Grid item container size={{ xs: 6 }}>
            <Grid item container size={{ xs: 12 }}>
              <Grid item container size={{ xs: 3 }}>
                <OmniTextField
                  label="Conversation"
                  type="textInput"
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"conversationNumber"}
                  value={userFeedbackRecord.conversationNumber}
                  disabled={true}
                />
              </Grid>
              <Grid item container size={{ xs: 4 }}>
                <OmniTextField
                  label="Sentiment"
                  type="textInput"
                  select
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"organizationType"}
                  value={userSentiment}
                  onChange={event => {
                    setUserSentiment(event.target.value);
                  }}
                >
                  <OmniMenuItem key={"negative"} value={"negative"}>
                    {"Negative"}
                  </OmniMenuItem>
                  <OmniMenuItem key={"positive"} value={"positive"}>
                    {"Positive"}
                  </OmniMenuItem>
                </OmniTextField>
              </Grid>

              <Grid item container size={{ xs: 5 }}>
                <OmniTextField
                  label="Feedback Status"
                  type="textInput"
                  select
                  name={uuid()} //prevents AutoComplete Suggestions
                  variant="outlined"
                  id={"organizationType"}
                  value={status}
                  onChange={event => {
                    setStatus(event.target.value);
                  }}
                >
                  {constants.feedbackStatus.map(orgType => (
                    <OmniMenuItem key={orgType.value} value={orgType.value}>
                      {orgType.label}
                    </OmniMenuItem>
                  ))}
                </OmniTextField>
              </Grid>
              <Grid item container size={{ xs: 12 }}>
                <OmniTextField
                  sx={{ width: "100%", marginTop: "4px" }}
                  label={"User Feedback"}
                  type="textInput"
                  variant="outlined"
                  name={uuid()} //prevents AutoComplete Suggestions
                  id={"message"}
                  rowCount={5}
                  multiline={true}
                  value={userFeedback}
                  onChange={event => setUserFeedback(event.target.value)}
                />
              </Grid>

              <Grid item container size={{ xs: 12 }}>
                <OmniTextField
                  sx={{ width: "100%", marginTop: "4px" }}
                  label={"Desired Response"}
                  type="textInput"
                  variant="outlined"
                  name={uuid()} //prevents AutoComplete Suggestions
                  id={"message"}
                  rowCount={4}
                  multiline={true}
                  value={response}
                  onChange={event => setResponse(event.target.value)}
                />
              </Grid>

              <Grid item container size={{ xs: 8 }}>
                <OmniTextField
                  sx={{ width: "100%", marginTop: "4px" }}
                  label={"Feedback From"}
                  type="textInput"
                  variant="outlined"
                  name={uuid()} //prevents AutoComplete Suggestions
                  id={"message"}
                  rowCount={7}
                  multiline={false}
                  value={userName}
                  onChange={event => setUserName(event.target.value)}
                />
              </Grid>

              <Grid item container size={{ xs: 4 }}>
                <Box
                  sx={{
                    display: editedUserFeedbackRecord.id == null ? "none" : "flex",
                    width: "100%",
                    flexDirection: "row",
                    marginTop: "4px",
                    justifyContent: "left",
                  }}
                >
                  <Switch
                    checked={archived}
                    onChange={() => {
                      setArchived(val => !val);
                    }}
                  />
                  <Typography variant="h6" sx={{ marginTop: "2px" }}>
                    Archived
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Box
          sx={{
            paddingRight: theme.spacing(2),
            paddingBottom: 0,
            display: "flex",
            flexDirection: "row",
            userSelect: "none",
            webkitUserSelect: "none",
            position: "relative",
            flexGrow: 1,
          }}
        >
          {userFeedbackRecord.id != null && props.onDelete != null && (
            <OmniResponsiveButton
              icon={<DeleteIcon />}
              onClick={() => setShowConfirm(true)}
              buttonText={deleteMessage}
              tooltipTitle=""
            />
          )}
        </Box>
        <Box
          sx={{
            flexGrow: 0,
            flexShrink: 1,
          }}
        >
          {cancelMessage && (
            <Button
              onClick={onCloseHandler}
              color="secondary"
              variant="contained"
              sx={{ marginRight: theme.spacing(2) }}
            >
              {cancelMessage}
            </Button>
          )}
          <Button onClick={onAcceptHandler} color="secondary" variant="contained">
            {okMessage}
          </Button>
        </Box>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want delete this Entry?"}
          onAccept={() => {
            setShowConfirm(false);
            onDeleteHandler();
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}
    </Dialog>
  );
};

export default BotpressUserFeedbackDialog;
